import React, { useState } from 'react';
import {
  ST_COLORS,
  PAYMENT_METHODS,
  stLabel,
  projLabel,
} from './constants';

// ── UnitEditModal ──
// Direct edit of a unit's status / client / pipeline. Used to fix data outside
// the standard "register deal" flow (e.g., correcting a manually-entered earnest,
// or moving a unit between custom statuses).
function UnitEditModal({ unit, team, onClose, onSave, t, lang }) {
  const [form, setForm] = useState({
    status: unit.status || 'متاح',
    current_price: unit.current_price || unit.base_price || 0,
    client_name: unit.client_name || '',
    client_phone: unit.client_phone || '',
    client_source: unit.client_source || '',
    payment_method: unit.payment_method || '',
    earnest_money: unit.earnest_money || 0,
    deal_stage: unit.deal_stage || 0,
    closer: unit.closer || '',
    afrag_date: unit.afrag_date || '',
    note: unit.note || '',
  });

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // When status flips back to "no client" (متاح / off-plan / unavailable), wipe
  // the deal fields so a stale closer/client doesn't get persisted when saving.
  const setStatus = (newStatus) => {
    const isNonClient = newStatus === 'متاح' || newStatus === 'بيع على الخارطة' || newStatus === 'غير متاح';
    setForm((p) => isNonClient
      ? { ...p, status: newStatus, client_name: '', client_phone: '', client_source: '',
          payment_method: '', earnest_money: 0, deal_stage: 0, closer: '', afrag_date: '', note: '' }
      : { ...p, status: newStatus });
  };
  const showClient = form.status !== 'متاح' && form.status !== 'بيع على الخارطة' && form.status !== 'غير متاح';
  const color = ST_COLORS[form.status] || 'var(--accent)';

  // Guard against silent earnest edits: if العربون changed, require an explicit
  // confirmation before persisting (the value is financial and was being
  // overwritten unnoticed). Other field edits save without a prompt.
  const handleSave = () => {
    const before = Number(unit.earnest_money || 0);
    const after = Number(form.earnest_money || 0);
    if (after !== before) {
      const msg = lang === 'ar'
        ? `تغيير العربون من ${before.toLocaleString('en-US')} إلى ${after.toLocaleString('en-US')} ريال.\nهل أنت متأكد؟`
        : `Change earnest from ${before.toLocaleString('en-US')} to ${after.toLocaleString('en-US')} SAR.\nAre you sure?`;
      if (!window.confirm(msg)) return;
    }
    onSave(unit.id, form);
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520, maxHeight: '92vh', overflowY: 'auto' }}
      >
        <div
          className="crm-modal-header"
          style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 12 }}
        >
          <div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', letterSpacing: 0.4, marginBottom: 2, textTransform: 'uppercase' }}>
              {t('udEdit')}
            </div>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
              {projLabel(unit.project, t)} — {unit.unit_code}
            </h2>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            &times;
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div className="input-group">
            <label>{t('udStatus')}</label>
            <select
              value={form.status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ borderInlineStart: '3px solid ' + color }}
            >
              {Object.keys(ST_COLORS).map((s) => (
                <option key={s} value={s}>
                  {stLabel(s, lang)}
                </option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label>{t('udPrice')} (SAR)</label>
            <input
              type="number"
              value={form.current_price}
              onChange={(e) => u('current_price', e.target.value)}
            />
          </div>
        </div>

        {showClient && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="input-group">
                <label>{t('udClient')}</label>
                <input value={form.client_name} onChange={(e) => u('client_name', e.target.value)} />
              </div>
              <div className="input-group">
                <label>{t('crmPhone') || (lang === 'ar' ? 'الجوال' : 'Phone')}</label>
                <input value={form.client_phone} onChange={(e) => u('client_phone', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="input-group">
                <label>{t('udPayment')}</label>
                <select value={form.payment_method} onChange={(e) => u('payment_method', e.target.value)}>
                  <option value="">—</option>
                  {PAYMENT_METHODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p[lang]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label>{t('udEarnest')} (SAR)</label>
                <input
                  type="number"
                  value={form.earnest_money}
                  onChange={(e) => u('earnest_money', e.target.value)}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="input-group">
                <label>{t('udCloser')}</label>
                <select value={form.closer} onChange={(e) => u('closer', e.target.value)}>
                  <option value="">—</option>
                  {team.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label>{lang === 'ar' ? 'المصدر/الوسيط' : 'Source/Broker'}</label>
                <input
                  value={form.client_source}
                  onChange={(e) => u('client_source', e.target.value)}
                />
              </div>
            </div>
            {/* Pipeline stage / closure checklist is managed in DealEditModal —
                this modal is now just for direct field edits (status, client,
                price, etc.). Reserved/sold/transferred units route through
                DealEditModal automatically (see pages/UnitsDeals.js). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div className="input-group">
                <label>{lang === 'ar' ? 'تاريخ الأفراغ' : 'Transfer Date'}</label>
                <input
                  value={form.afrag_date}
                  onChange={(e) => u('afrag_date', e.target.value)}
                  placeholder="DD-MM-YYYY"
                />
              </div>
              <div className="input-group">
                <label>{lang === 'ar' ? 'ملاحظة' : 'Note'}</label>
                <input value={form.note} onChange={(e) => u('note', e.target.value)} />
              </div>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
            {lang === 'ar' ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            style={{ flex: 2, fontWeight: 700 }}
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnitEditModal;
